/**
 * DEALSHAQ DOCUMENTATION GOVERNANCE SYSTEM
 * Phase 5: Governance, Versioning & Maintenance
 * 
 * This file contains:
 * - Release notes and change log entries
 * - Documentation versioning metadata
 * - Governance rules and policies
 * - Deprecation notices and migration guides
 */

// =============================================================================
// DOCUMENTATION VERSION METADATA
// =============================================================================
export const DOC_VERSION = {
  current: '2.0.0',
  releaseDate: '2026-03-30',
  lastUpdated: '2026-03-30',
  status: 'stable' // 'stable' | 'beta' | 'deprecated'
};

// =============================================================================
// RELEASE NOTES & CHANGE LOG
// =============================================================================
export const RELEASE_NOTES = [
  {
    version: '2.0.0',
    date: '2026-03-30',
    title: 'Documentation Hub Complete',
    type: 'major', // 'major' | 'minor' | 'patch'
    highlights: [
      'Full Documentation Hub with all 4 portals',
      'AI-powered search and help assistant',
      'Interactive tutorials and walkthroughs',
      'Multimedia training assets'
    ],
    sections: [
      {
        category: 'New Features',
        icon: 'Sparkles',
        changes: [
          {
            title: 'Phase 4: Multimedia & Training Assets',
            description: 'Added video tutorials, GIF demos, and interactive walkthroughs for all portals',
            portals: ['retailer', 'consumer', 'admin', 'charity'],
            docs: ['tutorials']
          },
          {
            title: 'Phase 3: AI Integration & Tooltips',
            description: 'Context-aware floating help button, inline tooltips, and AI-powered documentation search',
            portals: ['retailer', 'consumer', 'admin', 'charity'],
            docs: ['ai-help', 'tooltips']
          },
          {
            title: 'Phase 2: Full Portal Coverage',
            description: 'Extended documentation for all portal-specific features and workflows',
            portals: ['retailer', 'consumer', 'admin', 'charity'],
            docs: ['documentation']
          },
          {
            title: 'Phase 1: Documentation Foundation',
            description: 'Core documentation hub with portal selector, search, and glossary',
            portals: ['retailer', 'consumer', 'admin', 'charity'],
            docs: ['overview', 'glossary']
          }
        ]
      },
      {
        category: 'Improvements',
        icon: 'TrendingUp',
        changes: [
          {
            title: 'Multilingual Support for DealShaq Shopper App',
            description: 'Full 9-language support for consumer-facing documentation',
            portals: ['consumer'],
            docs: ['language']
          },
          {
            title: 'Shopping List Sourcing Documentation',
            description: 'Comprehensive guide for file upload OCR and AI-powered list processing',
            portals: ['consumer'],
            docs: ['dealshaq-agent']
          }
        ]
      },
      {
        category: 'Technical',
        icon: 'Code',
        changes: [
          {
            title: 'Auth Bypass for Help Pages',
            description: 'All portal help pages now accessible without authentication',
            portals: ['retailer', 'consumer', 'admin', 'charity'],
            docs: []
          }
        ]
      }
    ],
    breaking: [],
    deprecated: [],
    migration: null
  },
  {
    version: '1.5.0',
    date: '2026-03-20',
    title: 'Frontend Governance & CI/CD',
    type: 'minor',
    highlights: [
      'Complete Cypress E2E test suite',
      'GitHub Actions CI pipeline',
      'Jest unit test coverage',
      'Staging deployment configuration'
    ],
    sections: [
      {
        category: 'Testing',
        icon: 'CheckCircle',
        changes: [
          {
            title: '217 Jest Unit Tests',
            description: 'Comprehensive unit test coverage for all major components',
            portals: ['retailer', 'consumer', 'admin', 'charity'],
            docs: []
          },
          {
            title: '13 Cypress E2E Test Files',
            description: 'End-to-end test coverage for critical user flows',
            portals: ['retailer', 'consumer', 'admin', 'charity'],
            docs: []
          }
        ]
      },
      {
        category: 'Infrastructure',
        icon: 'Server',
        changes: [
          {
            title: 'Vercel + Railway Deployment',
            description: 'Staging environment configuration for frontend and backend',
            portals: [],
            docs: []
          }
        ]
      }
    ],
    breaking: [],
    deprecated: [],
    migration: null
  },
  {
    version: '1.0.0',
    date: '2026-02-15',
    title: 'Initial Platform Release',
    type: 'major',
    highlights: [
      'Multi-retailer shopping platform',
      'AI-powered DealShaq Shopping Assistant (DSA)',
      'Barcode scanning and OCR',
      'DoorDash delivery integration'
    ],
    sections: [
      {
        category: 'Core Features',
        icon: 'Rocket',
        changes: [
          {
            title: 'DealShaq Grocer App',
            description: 'Deal posting, order management, barcode scanning, analytics dashboard',
            portals: ['retailer'],
            docs: ['retailer-onboarding', 'retailer-barcode-scanning', 'retailer-order-management']
          },
          {
            title: 'DealShaq Shopper App',
            description: 'Deal discovery, AI shopping assistant, multi-store checkout',
            portals: ['consumer'],
            docs: ['consumer-shopping-checkout', 'consumer-dealshaq-agent']
          },
          {
            title: 'Admin Portal',
            description: 'Platform management, retailer approvals, incident handling',
            portals: ['admin'],
            docs: ['admin-incident-management', 'admin-retailer-approvals']
          },
          {
            title: 'DealShaq Charity App',
            description: 'Donation tracking, impact metrics, payout management',
            portals: ['charity'],
            docs: ['charity-donation-dashboard', 'charity-impact-metrics']
          }
        ]
      }
    ],
    breaking: [],
    deprecated: [],
    migration: null
  }
];

// =============================================================================
// DOCUMENT VERSION HISTORY (Per-Section Versioning)
// =============================================================================
export const DOC_VERSIONS = {
  // Retailer Docs
  'retailer-onboarding': {
    currentVersion: '2.0.0',
    versions: [
      { version: '2.0.0', date: '2026-03-30', author: 'DealShaq Team', summary: 'Added interactive walkthrough' },
      { version: '1.1.0', date: '2026-03-15', author: 'DealShaq Team', summary: 'Added Stripe Connect setup details' },
      { version: '1.0.0', date: '2026-02-15', author: 'DealShaq Team', summary: 'Initial release' }
    ]
  },
  'retailer-barcode-scanning': {
    currentVersion: '2.1.0',
    versions: [
      { version: '2.1.0', date: '2026-03-30', author: 'DealShaq Team', summary: 'Added 7 scan mode GIF demos' },
      { version: '2.0.0', date: '2026-03-25', author: 'DealShaq Team', summary: 'Added OCR price extraction docs' },
      { version: '1.0.0', date: '2026-02-15', author: 'DealShaq Team', summary: 'Initial release' }
    ]
  },
  'retailer-order-management': {
    currentVersion: '2.0.0',
    versions: [
      { version: '2.0.0', date: '2026-03-30', author: 'DealShaq Team', summary: 'Added order flow walkthrough' },
      { version: '1.0.0', date: '2026-02-15', author: 'DealShaq Team', summary: 'Initial release' }
    ]
  },
  'retailer-item-posting': {
    currentVersion: '1.1.0',
    versions: [
      { version: '1.1.0', date: '2026-03-20', author: 'DealShaq Team', summary: 'Added deal level explanations' },
      { version: '1.0.0', date: '2026-02-15', author: 'DealShaq Team', summary: 'Initial release' }
    ]
  },
  // Consumer Docs
  'consumer-dealshaq-agent': {
    currentVersion: '2.2.0',
    versions: [
      { version: '2.2.0', date: '2026-03-30', author: 'DealShaq Team', summary: 'Added AI shopping walkthrough' },
      { version: '2.1.0', date: '2026-03-28', author: 'DealShaq Team', summary: 'Added file upload/OCR documentation' },
      { version: '2.0.0', date: '2026-03-20', author: 'DealShaq Team', summary: 'Added constraint setting docs' },
      { version: '1.0.0', date: '2026-02-15', author: 'DealShaq Team', summary: 'Initial release' }
    ]
  },
  'consumer-shopping-checkout': {
    currentVersion: '2.0.0',
    versions: [
      { version: '2.0.0', date: '2026-03-30', author: 'DealShaq Team', summary: 'Added first purchase walkthrough' },
      { version: '1.0.0', date: '2026-02-15', author: 'DealShaq Team', summary: 'Initial release' }
    ]
  },
  // Admin Docs
  'admin-incident-management': {
    currentVersion: '2.0.0',
    versions: [
      { version: '2.0.0', date: '2026-03-30', author: 'DealShaq Team', summary: 'Added resolution walkthrough' },
      { version: '1.0.0', date: '2026-03-20', author: 'DealShaq Team', summary: 'Initial release' }
    ]
  },
  // Charity Docs
  'charity-donation-dashboard': {
    currentVersion: '2.0.0',
    versions: [
      { version: '2.0.0', date: '2026-03-30', author: 'DealShaq Team', summary: 'Added dashboard walkthrough' },
      { version: '1.0.0', date: '2026-03-20', author: 'DealShaq Team', summary: 'Initial release' }
    ]
  }
};

// =============================================================================
// GOVERNANCE RULES & POLICIES
// =============================================================================
export const GOVERNANCE_POLICIES = {
  overview: {
    title: 'Documentation Governance Overview',
    description: 'Standards and procedures for maintaining DealShaq documentation',
    lastUpdated: '2026-03-30',
    sections: [
      {
        id: 'purpose',
        title: 'Purpose',
        content: `This governance framework ensures that DealShaq documentation remains accurate, up-to-date, and useful for all users. It establishes clear processes for creating, reviewing, updating, and deprecating documentation.`
      },
      {
        id: 'scope',
        title: 'Scope',
        content: `These policies apply to all documentation within the DealShaq Documentation Hub, including:
- Portal-specific guides (Retailer, Consumer, Admin, Charity)
- API documentation
- Tutorials and training materials
- Glossary and reference materials
- Release notes and changelogs`
      }
    ]
  },
  
  updateRules: {
    title: 'Update Rules & Review Process',
    rules: [
      {
        id: 'rule-1',
        title: 'Feature Documentation Requirement',
        description: 'All new features must have corresponding documentation before release',
        priority: 'required',
        enforcement: 'PR review checklist'
      },
      {
        id: 'rule-2',
        title: 'Version Increment Guidelines',
        description: 'Documentation versions follow semantic versioning: Major (breaking changes), Minor (new content), Patch (fixes/clarifications)',
        priority: 'required',
        enforcement: 'Automated version tracking'
      },
      {
        id: 'rule-3',
        title: 'Review Cycle',
        description: 'All documentation must be reviewed quarterly for accuracy',
        priority: 'recommended',
        enforcement: 'Quarterly review calendar'
      },
      {
        id: 'rule-4',
        title: 'Multilingual Sync',
        description: 'Consumer portal translations must be updated within 2 weeks of English content changes',
        priority: 'required',
        enforcement: 'Translation tracking system'
      },
      {
        id: 'rule-5',
        title: 'Screenshot Currency',
        description: 'UI screenshots must be updated within 1 release cycle of visual changes',
        priority: 'recommended',
        enforcement: 'Visual diff checks'
      }
    ],
    reviewProcess: {
      steps: [
        { step: 1, title: 'Draft', description: 'Author creates or updates documentation' },
        { step: 2, title: 'Technical Review', description: 'Engineering team verifies accuracy' },
        { step: 3, title: 'Editorial Review', description: 'Content team checks clarity and style' },
        { step: 4, title: 'Stakeholder Approval', description: 'Product owner signs off' },
        { step: 5, title: 'Publish', description: 'Documentation goes live with version increment' }
      ],
      sla: {
        draft: '3 business days',
        technicalReview: '2 business days',
        editorialReview: '2 business days',
        approval: '1 business day'
      }
    }
  },

  styleGuide: {
    title: 'Documentation Style Guide',
    guidelines: [
      {
        category: 'Voice & Tone',
        rules: [
          'Use active voice ("Click the button" not "The button should be clicked")',
          'Be concise and direct',
          'Maintain professional but friendly tone',
          'Avoid jargon unless defined in glossary'
        ]
      },
      {
        category: 'Formatting',
        rules: [
          'Use numbered lists for sequential steps',
          'Use bullet points for non-sequential items',
          'Bold key terms on first use',
          'Use code formatting for UI element names, API endpoints, and technical terms'
        ]
      },
      {
        category: 'Structure',
        rules: [
          'Start with overview/purpose',
          'Include prerequisites if applicable',
          'Provide step-by-step instructions',
          'Add FAQs for common questions',
          'Link to related documentation'
        ]
      },
      {
        category: 'Accessibility',
        rules: [
          'Provide alt text for all images',
          'Use descriptive link text (not "click here")',
          'Ensure color is not the only differentiator',
          'Support screen reader navigation'
        ]
      }
    ]
  },

  languagePolicy: {
    title: 'Language & Localization Policy',
    policies: {
      retailer: {
        portal: 'Retailer',
        languages: ['English'],
        policy: 'English-only',
        reason: 'Business communication standardization'
      },
      consumer: {
        portal: 'Consumer',
        languages: ['English', 'Spanish', 'French', 'German', 'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Vietnamese'],
        policy: 'Full multilingual',
        reason: 'Diverse consumer base accessibility'
      },
      admin: {
        portal: 'Admin',
        languages: ['English'],
        policy: 'English-only',
        reason: 'Internal operations consistency'
      },
      charity: {
        portal: 'Charity',
        languages: ['English'],
        policy: 'English-only',
        reason: 'Non-profit partner communication'
      }
    }
  }
};

// =============================================================================
// DEPRECATION NOTICES
// =============================================================================
export const DEPRECATION_NOTICES = [
  // Example deprecation notice structure
  // {
  //   id: 'dep-001',
  //   title: 'Legacy Scanner Mode',
  //   status: 'deprecated', // 'announced' | 'deprecated' | 'removed'
  //   announcedDate: '2026-03-01',
  //   deprecationDate: '2026-04-01',
  //   removalDate: '2026-06-01',
  //   affectedPortals: ['retailer'],
  //   affectedDocs: ['retailer-barcode-scanning'],
  //   reason: 'Replaced by new unified scanner with better performance',
  //   migration: {
  //     title: 'Migrating to New Scanner',
  //     steps: [
  //       'Update to app version 2.0+',
  //       'Scanner will automatically use new mode',
  //       'Review new scan modes documentation'
  //     ],
  //     link: '/retailer/help?doc=retailer-barcode-scanning'
  //   }
  // }
];

// =============================================================================
// FEEDBACK CONFIGURATION
// =============================================================================
export const FEEDBACK_CONFIG = {
  categories: [
    { id: 'helpful', label: 'Helpfulness', icon: 'ThumbsUp' },
    { id: 'accuracy', label: 'Accuracy', icon: 'CheckCircle' },
    { id: 'clarity', label: 'Clarity', icon: 'Eye' },
    { id: 'completeness', label: 'Completeness', icon: 'FileText' }
  ],
  issueTypes: [
    { id: 'outdated', label: 'Outdated information', priority: 'high' },
    { id: 'incorrect', label: 'Incorrect information', priority: 'critical' },
    { id: 'unclear', label: 'Unclear explanation', priority: 'medium' },
    { id: 'missing', label: 'Missing information', priority: 'medium' },
    { id: 'broken-link', label: 'Broken link', priority: 'high' },
    { id: 'typo', label: 'Typo or grammar', priority: 'low' },
    { id: 'suggestion', label: 'Suggestion for improvement', priority: 'low' },
    { id: 'other', label: 'Other', priority: 'medium' }
  ],
  ratingScale: {
    min: 1,
    max: 5,
    labels: {
      1: 'Not helpful',
      2: 'Slightly helpful',
      3: 'Somewhat helpful',
      4: 'Very helpful',
      5: 'Extremely helpful'
    }
  }
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get release notes for a specific version
 */
export const getReleaseNotes = (version = null) => {
  if (version) {
    return RELEASE_NOTES.find(r => r.version === version);
  }
  return RELEASE_NOTES;
};

/**
 * Get latest release notes
 */
export const getLatestRelease = () => {
  return RELEASE_NOTES[0];
};

/**
 * Get version history for a document
 */
export const getDocVersionHistory = (docId) => {
  return DOC_VERSIONS[docId] || null;
};

/**
 * Get all active deprecation notices
 */
export const getActiveDeprecations = () => {
  const now = new Date();
  return DEPRECATION_NOTICES.filter(d => {
    const removalDate = new Date(d.removalDate);
    return removalDate > now;
  });
};

/**
 * Check if a document has a deprecation notice
 */
export const getDocDeprecation = (docId) => {
  return DEPRECATION_NOTICES.find(d => d.affectedDocs.includes(docId));
};

/**
 * Get governance rules by category
 */
export const getGovernanceRules = (category = null) => {
  if (category) {
    return GOVERNANCE_POLICIES[category] || null;
  }
  return GOVERNANCE_POLICIES;
};

export default {
  DOC_VERSION,
  RELEASE_NOTES,
  DOC_VERSIONS,
  GOVERNANCE_POLICIES,
  DEPRECATION_NOTICES,
  FEEDBACK_CONFIG,
  getReleaseNotes,
  getLatestRelease,
  getDocVersionHistory,
  getActiveDeprecations,
  getDocDeprecation,
  getGovernanceRules
};
